import clsx from 'clsx'
import React, { FocusEvent, FunctionComponent, isValidElement, ReactNode, useEffect, useRef, useState } from 'react'

import { useThemeClass } from '../Theme'
import { noop } from '../utils/function'
import { getChildProps } from '../utils/react'
import { DropdownMenuBorderStyle, MENU_ITEM_DISPLAY_NAME, MenuItemMarkup } from './constants'
import { MenuItem, MenuItemProps } from './MenuItem'
import styles from './style.module.scss'

export interface MenuProps {
  id?: string
  scrollIntoView?: boolean
  inline?: boolean
  hasHeightLimit?: boolean
  onSelect?: (value: string) => any

  testNameSpace?: string
  tabIndex?: number
  defaultValue?: string
  children?: ReactNode

  'aria-disabled'?: boolean
  'aria-labelledby'?: string
  'aria-describedby'?: string
  'aria-activedescendant'?: string
  onBlur?: (event: FocusEvent<HTMLUListElement>) => any
  borderStyle?: DropdownMenuBorderStyle
}

const defaultID = Math.random().toString(36)

export const Menu: FunctionComponent<MenuProps> = ({
  onSelect = noop,
  tabIndex = 0,
  inline = false,
  hasHeightLimit = true,
  id = defaultID,
  children,
  scrollIntoView = false,
  defaultValue = '',
  testNameSpace = '',
  onBlur = () => {},
  borderStyle = DropdownMenuBorderStyle.DEFAULT,
  ...otherProps
}) => {
  /* eslint-disable react-hooks/rules-of-hooks */
  if (typeof children === 'undefined') {
    return null
  }

  const ref = useRef<HTMLDivElement>(null)
  const [value, setValue] = useState(defaultValue)
  const [focus, setFocus] = useState(false)
  const themeClass = useThemeClass()

  useEffect(() => {
    scrollIntoView && ref?.current?.scrollIntoView({ behavior: 'smooth' })
  }, [scrollIntoView])

  /* eslint-enable react-hooks/rules-of-hooks */

  const componentStyle = clsx(styles.menu, focus && styles.focus, hasHeightLimit && styles.withMaxHeight, styles[themeClass])
  const wrapperStyle = clsx(styles.componentWrapper, inline && styles.inline, styles[`${themeClass}`], styles[borderStyle])

  const handleClick = (value: string): void => {
    setFocus(false)
    setValue(value)
    onSelect(value)
  }

  const handleBlur = (e: FocusEvent<HTMLUListElement>): void => {
    setFocus(false)
    onBlur(e)
  }

  const handleFocus = (): void => {
    setFocus(true)
  }

  const renderChildren = (): ReactNode => {
    const childrenList = React.Children.toArray(children)
    return childrenList.map((child, index) => {
      if (!isValidElement(child)) {
        return null
      }

      // @ts-expect-error
      const isMenuItem = child.type.displayName === MENU_ITEM_DISPLAY_NAME

      if (!isMenuItem) {
        return child
      }

      const childProps: MenuItemProps = getChildProps(child)
      const childId = `${id}_${childProps.value ?? index}`
      const selected = childProps.value === value
      return (
        <MenuItem
          key={childId}
          id={childId}
          idValue={childId}
          selected={selected}
          renderAs={MenuItemMarkup.li}
          onClick={handleClick}
          {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-dropdown-menu-item` })}
          {...childProps}
        />
      )
    })
  }

  return (
    <div className={wrapperStyle} ref={ref} {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-dropdown-menu-wrapper` })}>
      <ul
        id={id}
        role='listbox'
        tabIndex={tabIndex}
        className={componentStyle}
        aria-activedescendant={`${id}_${value}`}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-dropdown-menu` })}
        {...otherProps}
      >
        {renderChildren()}
      </ul>
    </div>
  )
}
